import { Sentry } from './lib/sentry'
import './lib/stripe-loader'

import { Modal, Tab } from "bootstrap"

declare global {
    interface Window {
        stripe?: import('@stripe/stripe-js').Stripe | null
        stripePromise?: Promise<import('@stripe/stripe-js').Stripe | null>
        Sentry: typeof Sentry | null
        Alpine: typeof Alpine
    }
}

import './modules/navigation'
import './modules/lazyLoad'

// Call this early to ensure Stripe is loaded before any other stripe imports
// Absorb rejection so a Stripe CDN failure doesn't halt the rest of app initialisation
await (window as any).stripePromise.catch(() => {})

import Alpine from "alpinejs"
import collapse from "@alpinejs/collapse"

window.Alpine = Alpine

Alpine.plugin(collapse)

// Async load Alpine components to keep the main file lightweight
;(async () => {
    const alpineComponents: Promise<unknown>[] = []

    // Load signup Alpine component only after Stripe is ready, to avoid Chrome TLA/import issues
    if (document.querySelector('[x-data^="signupForm"]')) {
        alpineComponents.push((async () => {
            try {
                const form = await import('./components/signup')
                Alpine.data('signupForm', form.default)
            } catch (error) {
                console.log(error)
            }
        })())
    }

    if (document.querySelector('[x-data^="contactForm"]')) {
        alpineComponents.push((async () => {
            try {
                const form = await import('./components/contact')
                Alpine.data('contactForm', form.default)
            } catch (error) {
                console.log(error)
            }
        })())
    }

    await Promise.all(alpineComponents)

    Alpine.start()
})()

/**
 * Dynamic setup stripe forms
 */
import('./modules/forms/stripe').then( (stripe) => {
    stripe.initialize(document)
}).catch( (error) => {
    Sentry.withScope( (scope: Sentry.Scope) => {
        scope.setTag('import', 'default-stripe')
        Sentry.captureException(error)
    })
})

import('./modules/forms/basic').then( (forms) => {
    forms.listeners(document)
}).catch( (error) => {
    Sentry.withScope( (scope: Sentry.Scope) => {
        scope.setTag('import', 'forms')
        Sentry.captureException(error)
    })
})

const sliders = document.querySelectorAll('.slick')
if (sliders.length > 0) {
    import('./modules/slider').then( (module) => {
        module.sliders()
    }).catch( (error) => {
        Sentry.withScope( (scope: Sentry.Scope) => {
            scope.setTag('import', 'slick')
            Sentry.captureException(error)
        })
    })
}

/**
 * Set up any Google Maps
 */
import './modules/googleMap';

/**
 * Import Bootstrap components
 */
const modals = document.querySelectorAll('.modal')
if (modals.length > 0) {
    modals.forEach( (modal) => {
        new Modal(modal)
    })
}

// Manipulate the URL for signup card
const pills = document.querySelectorAll('.nav-pills.plan-pills .nav-link')
pills.forEach( (element: HTMLElement) => {
    element.addEventListener('shown.bs.tab', ev => {
        const elem = <HTMLElement>ev.target
        const link = <HTMLAnchorElement>elem.closest('.card').querySelector('a')

        link.href = elem.dataset.url
    })

})

// Load Fancybox if needed
if (document.querySelectorAll('[data-fancybox], [data-fancybox-alerts], [data-fancybox-steps]').length > 0) {
    import('./modules/fancybox').then( (module) => {
        module.fancybox()
    }).catch( (error) => {
        Sentry.withScope( (scope: Sentry.Scope) => {
            scope.setTag('import', 'fancybox')
            Sentry.captureException(error)
        })
    })
}

// Load dynamic filters is needed
if (document.getElementById('land-sale-filter')) {
    import('./modules/dynamicLoadFilters').then( (dynamicFilters) => {
        dynamicFilters.setFilters()
    }).catch( (error) => {
        Sentry.withScope( (scope: Sentry.Scope) => {
            scope.setTag('import', 'dynamic-filters')
            Sentry.captureException(error)
        })
    })
}

/**
 * Homepage imaging
 */
const mappingPromo = document.querySelectorAll('.map-module-row')
if (mappingPromo) {
    mappingPromo.forEach( (elem: HTMLElement) => {
        elem.addEventListener("mouseover", () => {
            // If not the active element then take action
            if (!elem.classList.contains('map-module-row-active')) {
                const imageElement = <HTMLImageElement>document.querySelector('#map-module-image')

                // Get the active and disable
                const active = document.querySelector('.map-module-row-active')
                active.classList.remove('map-module-row-active')

                // Set the active
                elem.classList.add('map-module-row-active')
                imageElement.src = elem.dataset.lazyPreloadImage
            }
        }, true)
    })
}

const leaseForm = document.querySelector('.lease-form')
if (leaseForm) {
    import('./posting')
}


/**
 * CSS Map plugin
 */
const map = <HTMLDivElement>document.querySelector('.cssmap-container')
if (map) {
    // Load IF visible
    if (map.offsetParent) {
        import('./modules/map/map').then( (cssMap) => {
            cssMap.setup()
        }).catch( (error) => {
            Sentry.withScope( (scope: Sentry.Scope) => {
                scope.setTag('import', 'css-map')
                Sentry.captureException(error)
            })
        })
    }

    // Listen for resize and load IF visible
    window.addEventListener('resize', () => {
        if (map.offsetParent) {
            import('./modules/map/map').then( (cssMap) => {
                cssMap.setup()
            }).catch( (error) => {
                Sentry.withScope( (scope: Sentry.Scope) => {
                    scope.setTag('import', 'css-map')
                    Sentry.captureException(error)
                })
            })
        }
    }, true)
}


